import { useEffect, useRef, useState, useCallback } from "react";
import { apiClient, clearSession } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const IDLE_MIN = 30;
const WARN_MIN = 25;
const ACT_KEY = "ntn_last_activity";
const LOGOUT_KEY = "ntn_forced_logout";
export const EXPIRED_MSG_KEY = "ntn_session_expired_msg";

const now = () => Date.now();
const lastActivity = () => Number(localStorage.getItem(ACT_KEY) || 0);

export default function SessionGuard() {
  const { user, logout } = useAuth();
  const [warn, setWarn] = useState(false);
  const warnRef = useRef(false);

  const expire = useCallback(async (reason) => {
    try { await apiClient.post("/auth/logout"); } catch (_e) { /* noop */ }
    clearSession();
    try {
      localStorage.setItem(EXPIRED_MSG_KEY, "Your session expired for security. Please sign in again.");
      localStorage.setItem(LOGOUT_KEY, String(now()) + ":" + reason);
    } catch (_e) { /* noop */ }
    window.location.assign("/login?expired=1");
  }, []);

  // Record legitimate user interaction (throttled) — UX timer only; the
  // server enforces the real boundary via 30-min token/refresh windows.
  useEffect(() => {
    if (!user) return undefined;
    let last = 0;
    const note = () => {
      const t = now();
      if (t - last > 15000) { last = t; try { localStorage.setItem(ACT_KEY, String(t)); } catch (_e) { /* noop */ } }
    };
    note();
    const evs = ["click", "keydown", "scroll", "pointerdown"];
    evs.forEach((e) => window.addEventListener(e, note, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, note));
  }, [user]);

  // Silent server-side renewal — ONLY when real activity happened recently.
  useEffect(() => {
    if (!user) return undefined;
    const iv = setInterval(async () => {
      const idleMs = now() - lastActivity();
      if (idleMs < 10 * 60 * 1000) {
        try { await apiClient.post("/auth/refresh"); } catch (_e) { /* interceptor handles 401 */ }
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(iv);
  }, [user]);

  // Watchdog: warning at 25 min idle, hard sign-out at 30 min. Cross-tab via
  // the shared localStorage activity key and forced-logout broadcast.
  useEffect(() => {
    if (!user) return undefined;
    const iv = setInterval(() => {
      const idleMin = (now() - lastActivity()) / 60000;
      if (idleMin >= IDLE_MIN) { expire("idle"); }
      else if (idleMin >= WARN_MIN && !warnRef.current) { warnRef.current = true; setWarn(true); }
      else if (idleMin < WARN_MIN && warnRef.current) { warnRef.current = false; setWarn(false); }
    }, 5000);
    const onStorage = (e) => { if (e.key === LOGOUT_KEY) window.location.assign("/login?expired=1"); };
    window.addEventListener("storage", onStorage);
    return () => { clearInterval(iv); window.removeEventListener("storage", onStorage); };
  }, [user, expire]);

  const staySignedIn = async () => {
    try {
      // Server validates the session is still live; merely showing the
      // warning never extends anything.
      await apiClient.post("/auth/refresh", null, { headers: { "X-Session-Extend": "1" } });
      try { localStorage.setItem(ACT_KEY, String(now())); } catch (_e) { /* noop */ }
      warnRef.current = false; setWarn(false);
    } catch (_e) { expire("idle"); }
  };

  if (!user || !warn) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center" data-testid="session-expiry-warning">
      <div className="bg-white rounded-sm shadow-xl max-w-md w-full mx-4 p-6 border border-[#0A192F]/10">
        <h2 className="text-lg font-semibold text-[#0A192F]">Session expiring soon</h2>
        <p className="mt-2 text-sm text-[#4A5568]">
          Your session will expire in 5 minutes due to inactivity.
        </p>
        <div className="mt-5 flex gap-3 justify-end">
          <Button variant="outline" className="rounded-sm" data-testid="session-sign-out-btn"
                  onClick={async () => { await logout(); clearSession(); window.location.assign("/login"); }}>
            Sign Out
          </Button>
          <Button className="rounded-sm bg-[#0A192F] text-white" data-testid="session-stay-signed-in-btn"
                  onClick={staySignedIn}>
            Stay Signed In
          </Button>
        </div>
      </div>
    </div>
  );
}
